import s from '#modules/facets/facet.module.scss';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { Facet } from '#types/generated/graphql-types';
import Button from '#components/controls/button/Button';
import ButtonTransparent from '#components/controls/buttons/button/Button';
import Typography from '#components/typography/Typography';
import { useQueryFilters } from '#hooks/useQueryFilters';
import Search from '#modules/search/Search';
import Drawer from '#components/navigation/drawer/Drawer';
import Menu from '#components/menu/Menu';
import { createPortal } from 'react-dom';
import IconButton from '#components/controls/icon-button/IconButton';
import useTranslation from 'next-translate/useTranslation';
import { Clear, FacetIcon } from '#components/icons';
import { useIsSearch } from '#hooks/useIsSearch';
import { useOvermindActions, useOvermindState } from '#store/overmind';
import CountFacet from './CountFacet';
import { State } from '#store/namespaces/search/state';

type Props = {
  loading: boolean;
  facets: Facet[];
};

const Facets = ({ loading, facets }: Props) => {
  const { search: searchActions } = useOvermindActions();
  const {
    search: { filters: overmindFilters, countResult, query: searchQuery },
  } = useOvermindState();

  const router = useRouter();
  const [isFiltersBarOpen, setIsFiltersBarOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const { filters: selectedFiltersFromURL } = useQueryFilters();
  const { searchUrl } = useIsSearch();
  const { t } = useTranslation();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Initialize/update overmindFilters (mostly needed on the initial load)
  // Moves selected filters specified in the URL to the overmind state
  useEffect(() => {
    if (loading) return;

    // Start with an empty payload.
    const payload: Partial<State['filters']> = {
      years: new Set(),
      categorySlugs: new Set(),
    };
    Object.keys(overmindFilters).forEach((storeKey) => {
      if (storeKey !== 'years' && storeKey !== 'categorySlugs') {
        payload[storeKey] = new Set();
      }
    });

    // Set Categories
    if (selectedFiltersFromURL.categorySlugs) {
      payload.categorySlugs = new Set(selectedFiltersFromURL.categorySlugs);
    }

    // Set Years
    if (selectedFiltersFromURL.years) {
      payload.years = new Set(selectedFiltersFromURL.years);
    }

    // Set Dynamic Filters from URL
    Object.keys(selectedFiltersFromURL).forEach((key) => {
      if (['years', 'categorySlugs'].includes(key)) return;

      const val = selectedFiltersFromURL[key];
      if (Array.isArray(val)) {
        payload[key] = new Set(val);
      } else if (typeof val === 'string') {
        payload[key] = new Set([val]);
      }
    });

    // Update the Overmind Store
    searchActions.updateFacetFilters(payload);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, selectedFiltersFromURL, searchActions]);

  const handleFacetChange = (facetName: string, values: Set<string>) => {
    searchActions.updateFacetFilters({ [facetName]: values });
  };

  const handleReset = () => {
    const resetPayload: Record<string, Set<string>> = {};
    Object.keys(overmindFilters).forEach((k) => {
      resetPayload[k] = new Set();
    });
    searchActions.updateFacetFilters(resetPayload);
  };

  const handleSubmit = (
    event?: FormEvent<HTMLFormElement> | React.MouseEvent
  ) => {
    event?.preventDefault();

    const query = { ...router.query };
    if (searchQuery) {
      query.needle = searchQuery;
    } else {
      delete query.needle;
    }

    // TODO: delete later
    delete query.dateFrom;
    delete query.dateTo;

    // Sync Years, Categories & Dynamic Facets
    Object.keys(overmindFilters).forEach((key) => {
      // TODO: delete later
      if (key === 'dates') return;

      const valueSet = overmindFilters[key];

      // Map internal Store key -> URL param name
      // e.g., 'categorySlugs' -> 'category'
      const paramName = key === 'categorySlugs' ? 'category' : key;

      if (valueSet instanceof Set && valueSet.size > 0) {
        query[paramName] = Array.from(valueSet).join('.');
      } else {
        delete query[paramName];
      }
    });

    setIsFiltersBarOpen(false);
    router.push({ pathname: searchUrl, query });
  };

  const numberOfSelectedFilters = useMemo(() => {
    let count = 0;
    Object.keys(overmindFilters).forEach((key) => {
      const value = overmindFilters[key];
      if (value instanceof Set) count += value.size;
      if (Array.isArray(value)) count += value.length;
    });
    return count;
  }, [overmindFilters]);

  const isFilterDisabled = facets.length === 0 && numberOfSelectedFilters === 0;

  return (
    <div className={s.host}>
      <div className={s.facetContainer}>
        <div className={s.searchWrapper}>
          <div className={s.searchContainer}>
            <Search />
          </div>
        </div>
        <ButtonTransparent
          disabled={isFilterDisabled}
          transparent
          type="reset"
          text={t('filter')}
          onClick={() => setIsFiltersBarOpen(true)}
          endIcon={!numberOfSelectedFilters && <FacetIcon />}
          title={'filters'}
          tag={numberOfSelectedFilters > 0 && `${numberOfSelectedFilters}`}
          isActive={numberOfSelectedFilters > 0}
        />
      </div>

      {isMounted &&
        !loading &&
        createPortal(
          <Drawer
            classes={s.hostDrawer}
            position="right"
            open={isFiltersBarOpen}
            onClose={() => setIsFiltersBarOpen(false)}
            style={{ zIndex: 1000, position: 'fixed' }}
          >
            <div className={s.container}>
              <div className="tw-mb-4 tw-grid tw-grid-flow-col tw-items-center">
                <Typography variant="h3" classes="tw-ml-1 tw-font-normal">
                  {`${t('filter')}`}
                </Typography>
                <IconButton
                  transparent={true}
                  classes="tw-justify-self-end"
                  onClick={() => setIsFiltersBarOpen(false)}
                >
                  <Clear color="#fff" />
                </IconButton>
              </div>

              <div className={s.scroll}>
                <Menu>
                  {facets.map((facet, index) => {
                    if (
                      facet.__typename === 'DateTimeFacet' ||
                      facet.name === 'mediaTypeCount' ||
                      facet.name === 'category_ids'
                    ) {
                      return null;
                    }

                    if (facet.__typename === 'CountFacet') {
                      if (!facet.values || facet.values.length === 0)
                        return null;

                      const currentSet = overmindFilters[facet.name];
                      const selectedValues =
                        currentSet instanceof Set
                          ? currentSet
                          : new Set<string>();

                      return (
                        <CountFacet
                          key={facet.name || index}
                          loading={loading}
                          facet={facet}
                          selectedValues={selectedValues}
                          onChange={handleFacetChange}
                        />
                      );
                    }
                    return null;
                  })}
                </Menu>
              </div>

              <div className={s.apply}>
                <Button
                  fullWidth
                  variant="outlined"
                  type="reset"
                  title={t('filterFooterButtonsReset')}
                  onClick={handleReset}
                />
                <Button
                  fullWidth
                  onClick={handleSubmit}
                  type="submit"
                  title={`${t('filterFooterButtonsApply')} (${
                    countResult.count !== null ? countResult.count : '...'
                  })`}
                />
              </div>
            </div>
          </Drawer>,
          document.body
        )}
    </div>
  );
};

export default Facets;
